import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { ThumbUp, ThumbDown } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { NoteSectionFeedbackEvent2 } from '@store/generated/admin';
import { formatDateTime } from '../utils/formatting';

interface SectionFeedbackDisplayProps {
  event: NoteSectionFeedbackEvent2;
}

export function SectionFeedbackDisplay({ event }: SectionFeedbackDisplayProps) {
  const navigate = useNavigate();

  const handleModuleClick = () => {
    if (event.module_id) {
      navigate(`/admin-v2/feedback/sessions?module_id=${encodeURIComponent(event.module_id)}`);
    }
  };

  // Map rating to icon and label
  const getRatingDisplay = () => {
    switch (event.section_rating) {
      case 0:
        return {
          icon: <ThumbDown />,
          label: 'Negative',
          color: 'error' as const,
          borderColor: 'error.main',
        };
      case 1:
        return {
          icon: <ThumbUp />,
          label: 'Positive',
          color: 'success' as const,
          borderColor: 'success.main',
        };
      default:
        return {
          icon: <ThumbUp />,
          label: 'Unknown',
          color: 'default' as const,
          borderColor: 'grey.500',
        };
    }
  };

  const ratingDisplay = getRatingDisplay();

  // Extract section content properties
  const sectionContent = event.section_content;
  const heading =
    sectionContent?.name ||
    sectionContent?.heading ||
    event.section_id.substring(0, 20) ||
    'Section';
  const text = sectionContent?.text || '';

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: 4,
        borderLeftColor: ratingDisplay.borderColor,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header Section */}
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={2}>
          <Chip
            icon={ratingDisplay.icon}
            label={ratingDisplay.label}
            color={ratingDisplay.color}
            size="small"
            variant="outlined"
          />
          <Typography variant="subtitle2" fontWeight="bold">
            {heading}
          </Typography>
          {event.module_id && (
            <Chip
              label={`Module: ${event.module_id.substring(0, 12)}...`}
              size="small"
              variant="outlined"
              onClick={handleModuleClick}
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {formatDateTime(new Date(event.timestamp * 1000).toISOString())}
          </Typography>
        </Box>

        {/* Section Content */}
        {text && (
          <Box mb={2} sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              gutterBottom
              display="block"
            >
              Section Content:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {text}
            </Typography>
          </Box>
        )}

        {/* Comment Section */}
        {event.section_comment && (
          <Box>
            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
              Comment:
            </Typography>
            <Typography variant="body2" fontStyle="italic" color="text.secondary">
              {event.section_comment}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
